import React from "react";
import RepoBranchRows from "./RepoBranchRows";
import { analyzeProject, saveRepository, SAVE_ERROR_MESSAGES } from "./repoApi";

// ─── Save Repository Modal ───────────────────────────────────────────────
// After a project is generated (mule / springboot / dotnet), push the code to
// repositories — ONE repository per service — on GitHub or Azure Repos.
// Flow: analyze (list services) → check (per-row state, inside RepoBranchRows)
// → save. No pipeline runs. Credentials come from Connected Apps accounts.
// Backend: POST /api/v1/project/analyze, /api/v1/repository/check, /repository/save;
// progress via Socket.IO "repository:log". See FRONTEND_REPOSITORY_SAVE.md.
//
// Props: projectPaths, technology, sessionId, backendUrl, token, socketRef, projName,
//        githubAccounts=[], azureAccounts=[], connectHref="/settings", onClose, onComplete

const C = {
  bg: "#0e1219", panel: "#121825", border: "rgba(255,255,255,0.1)", borderHi: "rgba(59,127,255,0.4)",
  accent: "#3b7fff", text: "#dce8ff", sub: "#aac0dc", muted: "#7a93bc", good: "#00c98d", warn: "#f5a623", bad: "#ef4444",
};
const slug = (s) => (s || "").toLowerCase().trim().replace(/[^a-z0-9._-]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 100);

export default function SaveRepositoryModal({
  projectPaths, technology, sessionId, backendUrl, token, socketRef, projName,
  githubAccounts = [], azureAccounts = [], connectHref = "/settings", onClose, onComplete,
}) {
  const ghAccounts = Array.isArray(githubAccounts) ? githubAccounts : [];
  const azAccounts = (Array.isArray(azureAccounts) ? azureAccounts : []).filter((a) => a && (a.azDevopsPat || a.organization || a.organizationName));
  const source = React.useMemo(() => ({ type: "generated", projectPaths: Array.isArray(projectPaths) ? projectPaths : [] }), [projectPaths]);

  const [provider, setProvider] = React.useState("github");
  const [ghAccountId, setGhAccountId] = React.useState(ghAccounts[0] ? ghAccounts[0]._id : "");
  const [ghPrivate, setGhPrivate] = React.useState(true);
  const [azAccountId, setAzAccountId] = React.useState(azAccounts[0] ? azAccounts[0]._id : "");
  const [azProject, setAzProject] = React.useState(slug(projName));
  const [azVisibility, setAzVisibility] = React.useState("private");

  const [analyzing, setAnalyzing] = React.useState(true);
  const [analyzeError, setAnalyzeError] = React.useState(null);
  const [projects, setProjects] = React.useState([]);
  const [layout, setLayout] = React.useState("repo-per-service");

  const [repos, setRepos] = React.useState([]);       // [{ service, repo, branch?, baseBranch? }]
  const [reposValid, setReposValid] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [logs, setLogs] = React.useState([]);
  const [error, setError] = React.useState(null);
  const [result, setResult] = React.useState(null);

  // Credentials from the selected connected account.
  const ghSel = ghAccounts.find((a) => a._id === ghAccountId) || null;
  const ghOwner = ghSel ? (ghSel.gitUsername || ghSel.username || "") : "";
  const ghToken = ghSel ? (ghSel.gitPat || ghSel.token || "") : "";
  const azSel = azAccounts.find((a) => a._id === azAccountId) || null;
  const azOrgRaw = azSel ? (azSel.organization || azSel.organizationName || "") : "";
  const azOrgUrl = azOrgRaw ? (/^https?:\/\//i.test(azOrgRaw) ? azOrgRaw : `https://dev.azure.com/${azOrgRaw}`) : "";
  const azPat = azSel ? (azSel.azDevopsPat || "") : "";

  const hasAccount = provider === "github" ? ghAccounts.length > 0 : azAccounts.length > 0;
  const hasCreds = provider === "github" ? !!(ghSel && ghToken && ghOwner) : !!(azSel && azPat && azOrgUrl);
  const creds = provider === "github"
    ? { owner: ghOwner, token: ghToken }
    : { orgUrl: azOrgUrl, pat: azPat, project: (azProject || "").trim() || slug(projName) };

  // Analyze once — lists the services (provider-independent, reads the code).
  React.useEffect(() => {
    let alive = true;
    setAnalyzing(true); setAnalyzeError(null);
    analyzeProject(backendUrl, token, { sessionId, source }).then(({ ok, data }) => {
      if (!alive) return;
      if (ok && data && data.success) {
        setProjects(data.projects || []);
        const single = (data.projects || []).length === 1 ? data.projects[0] : null;
        setLayout(single && single.layouts ? (single.layouts.default || "repo-per-service") : "repo-per-service");
      } else {
        setAnalyzeError((data && (data.error || SAVE_ERROR_MESSAGES[data.code])) || "Could not analyze the generated project.");
      }
      setAnalyzing(false);
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backendUrl, token, sessionId, source]);

  // repository:log progress.
  React.useEffect(() => {
    const sock = socketRef && socketRef.current;
    if (!sock) return;
    const onLog = (res) => setLogs((prev) => [...prev, { message: (res && res.message) || "", level: (res && res.level) || "info" }]);
    sock.on("repository:log", onLog);
    return () => { try { sock.off("repository:log", onLog); } catch (e) {} };
  }, [socketRef]);

  const singleProject = projects.length === 1 ? projects[0] : null;
  const allowedLayouts = singleProject && singleProject.layouts ? (singleProject.layouts.allowed || ["repo-per-service"]) : ["repo-per-service"];
  const showLayout = allowedLayouts.length > 1;
  const notIncluded = (singleProject && singleProject.notIncludedInRepoPerService) || [];

  // Units for the rows: monorepo → one row for the whole project; else one per service.
  const units = React.useMemo(() => {
    if (layout === "monorepo" && singleProject) {
      return [{ service: singleProject.name, kind: "", suggestedRepo: slug(singleProject.name) }];
    }
    return projects.flatMap((p) => (p.units || []).map((u) => ({ service: u.service, kind: u.kind, suggestedRepo: u.suggestedRepo })));
  }, [projects, layout, singleProject]);

  const onRepos = React.useCallback((r, valid) => { setRepos(r); setReposValid(valid); }, []);

  const canSave = !saving && hasCreds && reposValid && units.length > 0;

  const doSave = () => {
    setError(null); setSaving(true); setLogs([]);
    const body = { sessionId, source, provider, layout };
    // The backend treats both "monorepo" and "single" (one-service project) as the
    // single-repo shape (github.repo / azure.repo), not the per-service repos[] array.
    const singleRepo = layout === "monorepo" || layout === "single";
    if (provider === "github") {
      const gh = { token: ghToken, owner: ghOwner, private: ghPrivate };
      if (singleRepo) { const r = repos[0] || {}; gh.repo = r.repo; if (r.branch) gh.branch = r.branch; if (r.baseBranch) gh.baseBranch = r.baseBranch; }
      else gh.repos = repos;
      body.github = gh;
    } else {
      const az = { orgUrl: azOrgUrl, pat: azPat, project: creds.project, projectVisibility: azVisibility };
      if (singleRepo) { const r = repos[0] || {}; az.repo = r.repo; if (r.branch) az.branch = r.branch; if (r.baseBranch) az.baseBranch = r.baseBranch; }
      else az.repos = repos;
      body.azureRepos = az;
    }
    saveRepository(backendUrl, token, body).then(({ ok, data }) => {
      if (ok && data && data.success) {
        setResult(data);
        onComplete && onComplete({
          sessionId, technology: data.technology, provider: data.provider, layout: data.layout,
          azureProject: data.azureProject ? { name: data.azureProject.name, url: data.azureProject.url } : undefined,
          repositories: (data.repositories || []).map((x) => ({ service: x.service, provider: data.provider, fullName: x.fullName, repo: x.name, branch: x.branch, url: x.url })),
        });
      } else {
        setError({ code: (data && data.code) || "PROVIDER_ERROR", message: (data && data.error) || SAVE_ERROR_MESSAGES[data && data.code] || "Save failed.", details: data && data.details });
      }
    }).finally(() => setSaving(false));
  };

  const input = { width: "100%", background: C.bg, color: C.text, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 9px", fontSize: 13, outline: "none", boxSizing: "border-box" };
  const label = { fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.04em", margin: "10px 0 4px" };
  const readonlyVal = { ...input, background: "rgba(255,255,255,0.03)", color: C.sub };

  const connectNotice = (what) => (
    <div style={{ marginTop: 10, padding: 12, background: "rgba(245,166,35,0.08)", border: `1px solid rgba(245,166,35,0.3)`, borderRadius: 8 }}>
      <div style={{ fontSize: 12.5, color: C.warn, marginBottom: 8 }}>No connected {what} account. Link one in Connected Apps to save here.</div>
      <a href={connectHref} style={{ display: "inline-block", padding: "7px 14px", borderRadius: 7, background: C.accent, color: "#fff", fontSize: 12, fontWeight: 700, textDecoration: "none" }}>Connect a {what} account →</a>
    </div>
  );

  return (
    <div className="sh-overlay" onClick={onClose}>
      <div className="sh-scroll" onClick={(e) => e.stopPropagation()} style={{ width: "min(600px, 94vw)", maxHeight: "90vh", overflowY: "auto", background: C.panel, border: `1px solid ${C.borderHi}`, borderRadius: 12 }}>
        <div style={{ display: "flex", alignItems: "center", padding: "14px 16px", borderBottom: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.text, flex: 1 }}>💾 Save to repository</div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: C.sub, fontSize: 18, cursor: "pointer" }}>✕</button>
        </div>

        {result ? (
          <div style={{ padding: 16 }}>
            <div style={{ color: C.good, fontWeight: 700, fontSize: 14, marginBottom: 10 }}>✅ Saved to {result.provider === "github" ? "GitHub" : "Azure Repos"}</div>
            {result.azureProject && (
              <div style={{ fontSize: 12, color: C.sub, marginBottom: 8 }}>Azure DevOps project: <a href={result.azureProject.url} target="_blank" rel="noreferrer" style={{ color: C.accent }}>{result.azureProject.name}</a></div>
            )}
            {(result.repositories || []).map((r, i) => (
              <div key={i} style={{ fontSize: 12.5, padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
                <a href={r.url} target="_blank" rel="noreferrer" style={{ color: C.accent, fontWeight: 600 }}>{r.fullName}</a>
                <span style={{ color: C.muted }}> · {r.branch}{r.alsoBranch ? ` (+${r.alsoBranch})` : ""} · {r.action} · commit {(r.commit || "").slice(0, 7)}</span>
              </div>
            ))}
            {(result.warnings || []).map((w, i) => <div key={i} style={{ fontSize: 11, color: C.warn, marginTop: 6 }}>⚠ {w}</div>)}
            <div style={{ fontSize: 11, color: C.muted, marginTop: 10 }}>No pipeline was run. These repositories are available to the Deployment Agent.</div>
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 7, background: C.accent, border: "none", color: "#fff", fontWeight: 700, cursor: "pointer" }}>Done</button>
            </div>
          </div>
        ) : (
          <div style={{ padding: 16 }}>
            <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              {[["github", "GitHub"], ["azure-repos", "Azure Repos"]].map(([v, lbl]) => (
                <button key={v} onClick={() => setProvider(v)} style={{ flex: 1, padding: "8px", borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: "pointer",
                  background: provider === v ? "rgba(59,127,255,0.18)" : "transparent", color: provider === v ? C.accent : C.sub, border: `1px solid ${provider === v ? C.borderHi : C.border}` }}>{lbl}</button>
              ))}
            </div>

            {provider === "github" ? (
              ghAccounts.length === 0 ? connectNotice("GitHub") : (
                <>
                  <div style={label}>GitHub account <span style={{ textTransform: "none", fontWeight: 400, color: C.muted }}>(from Connected Apps)</span></div>
                  <select style={input} value={ghAccountId} onChange={(e) => setGhAccountId(e.target.value)}>
                    {ghAccounts.map((a) => <option key={a._id} value={a._id}>{a.gitUsername || a.username || a.name || a._id}</option>)}
                  </select>
                  <div style={label}>Owner</div>
                  <input style={readonlyVal} value={ghOwner} readOnly />
                  {!ghToken && <div style={{ fontSize: 11, color: C.bad, marginTop: 3 }}>This account has no stored token — re-link it in Connected Apps.</div>}
                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: C.sub, marginTop: 10 }}>
                    <input type="checkbox" checked={ghPrivate} onChange={(e) => setGhPrivate(e.target.checked)} /> Private (new repositories)
                  </label>
                </>
              )
            ) : (
              azAccounts.length === 0 ? connectNotice("Azure DevOps") : (
                <>
                  <div style={label}>Azure DevOps account <span style={{ textTransform: "none", fontWeight: 400, color: C.muted }}>(from Connected Apps)</span></div>
                  <select style={input} value={azAccountId} onChange={(e) => setAzAccountId(e.target.value)}>
                    {azAccounts.map((a) => <option key={a._id} value={a._id}>{a.organizationName || a.organization || a._id}</option>)}
                  </select>
                  <div style={label}>Organization URL</div>
                  <input style={readonlyVal} value={azOrgUrl} readOnly />
                  {!azPat && <div style={{ fontSize: 11, color: C.bad, marginTop: 3 }}>This account has no stored PAT — re-link it in Connected Apps.</div>}
                  <div style={label}>Project <span style={{ textTransform: "none", fontWeight: 400, color: C.muted }}>(created if it doesn't exist)</span></div>
                  <input style={input} value={azProject} onChange={(e) => setAzProject(e.target.value)} placeholder={slug(projName)} />
                  <div style={label}>Project visibility</div>
                  <select style={input} value={azVisibility} onChange={(e) => setAzVisibility(e.target.value)}>
                    <option value="private">Private</option>
                    <option value="public">Public</option>
                  </select>
                </>
              )
            )}

            {showLayout && (
              <>
                <div style={label}>Save as</div>
                <select style={input} value={layout} onChange={(e) => setLayout(e.target.value)}>
                  <option value="repo-per-service">Separate repository per service</option>
                  {allowedLayouts.includes("monorepo") && <option value="monorepo">One repository (monorepo)</option>}
                </select>
              </>
            )}

            {hasAccount && (
              <div style={{ marginTop: 14, padding: 10, background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                {analyzing ? (
                  <div style={{ fontSize: 12, color: C.muted }}>Analyzing the generated project…</div>
                ) : analyzeError ? (
                  <div style={{ fontSize: 12, color: C.bad }}>{analyzeError}</div>
                ) : (
                  <RepoBranchRows provider={provider} sessionId={sessionId} backendUrl={backendUrl} token={token} creds={creds} units={units} projName={projName} onRepos={onRepos} />
                )}
                {notIncluded.length > 0 && layout !== "monorepo" && (
                  <div style={{ fontSize: 10.5, color: C.muted, marginTop: 4 }}>Not included (one repo per service): {notIncluded.join(", ")}</div>
                )}
              </div>
            )}

            {logs.length > 0 && (
              <div className="sh-scroll" style={{ marginTop: 10, maxHeight: 140, overflowY: "auto", background: "#0a0d13", border: `1px solid ${C.border}`, borderRadius: 8, padding: 8, fontFamily: "monospace", fontSize: 11.5 }}>
                {logs.map((l, i) => <div key={i} style={{ color: l.level === "error" ? C.bad : l.level === "warning" ? C.warn : C.sub }}>{l.message}</div>)}
              </div>
            )}

            {error && (
              <div style={{ marginTop: 10, fontSize: 12.5, color: C.warn }}>
                ⚠ {error.message}
                {error.details && error.details.field && <div style={{ color: C.bad, marginTop: 3 }}>{error.details.field}</div>}
              </div>
            )}

            <div style={{ fontSize: 11, color: C.muted, marginTop: 12 }}>No pipeline runs when saving. Pipelines are set up later in Deployment.</div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
              <button onClick={onClose} style={{ padding: "8px 16px", borderRadius: 7, background: "transparent", border: `1px solid ${C.border}`, color: C.sub, fontWeight: 700, cursor: "pointer" }}>Cancel</button>
              <button onClick={doSave} disabled={!canSave} style={{ padding: "8px 18px", borderRadius: 7, background: C.accent, border: "none", color: "#fff", fontWeight: 700, cursor: canSave ? "pointer" : "default", opacity: canSave ? 1 : 0.5 }}>
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
